import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { Comment, Task } from '../../../app/entities';
import { Editor } from '../../../components/editor/editor';
import { TaskPropertyButton } from './task-property.button';
import { Dot } from '../../process/components/step-status-selector';
import { StatusSelector } from '../../process/components/status-selector';
import { PriorityIcon, PrioritySelector } from '../../process/components/priority-selector';
import { priorities } from '../priorities';
import { AssigneeSelector } from '../../../components/assignee-selector/assignee-selector';
import { CreateTaskDateProperty } from './create-task-date-property';
import TaskStepSelector from './task-step-selector';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { AddComment } from '../../../components/comment/AddComment';
import { CommentItem } from '../../../components/comment/CommentItem';
import { HistoryInput } from '../../../components/history-input/history-input';

import styles from './task-detail.module.scss';
import { TaskSubscribers } from './task-subscribers';

interface TaskDetailProps {
  task: Task;
}

export const TaskDetail: React.FC<TaskDetailProps> = observer(({ task }) => {
  useEffect(() => {
    task.fetchComments();
  }, [task]);

  const handleChange = (data: object) => {
    task.update(data);
  };

  const handleAddComment = async (comment) => {
    task.addComment(comment);
    return {} as any;
  };

  return (
    <div className={styles.container}>
      <div className={styles.properties}>
        <StatusSelector
          onChange={(status) => handleChange({ status })}
          statusMap={Task.StatusMapper}
          status={task.status}
          isViewOnly={false}
          trigger={
            <TaskPropertyButton
              icon={<Dot data-status={task.status} />}
              label={Task.StatusMapper[task.status].title}
              isActive={!!task.status}
            />
          }
        />

        <PrioritySelector
          onChange={(priority) => handleChange({ priority })}
          priority={task.priority}
          withTitle
          trigger={
            <TaskPropertyButton
              icon={<PriorityIcon priority={task.priority} />}
              label={priorities[task.priority]}
              isActive={!!task.priority}
            />
          }
        />

        <AssigneeSelector
          project={task.project}
          selectedMemberId={task.assigneeId}
          onChange={(assigneeId) => handleChange({ assigneeId })}
        />

        <CreateTaskDateProperty
          value={task.dueDate}
          onChange={(dueDate) => handleChange({ dueDate })}
        />

        {task.projectId && (
          <TaskStepSelector
            project={task.project}
            onChange={(stepId) => handleChange({ stepId, projectId: task.projectId })}
            selectedStepId={task.stepId}
            withIcon
          />
        )}

        <TagsViewer
          targetType="task"
          overflow="wrap"
          alignment="start"
          onSelect={(tagsIds) => handleChange({ tagsIds })}
          tagsIds={task.tagsIds}
          fullWidth={false}
        />
      </div>

      <div className={styles.title}>
        <HistoryInput value={task.title} onChange={(title) => handleChange({ title })} />
      </div>

      <Editor
        content={task.description}
        onChange={(description) => task.update({ description })}
        placeholder="Add description..."
      />

      <hr className={styles.divider} />

      <div className={styles['discussion-header']}>
        <h3>Discussion</h3>
        <TaskSubscribers task={task} />
      </div>

      <div>
        {task.comments
          .filter((comment) => !comment.parentId)
          .map((comment) => (
            <div key={comment._id} className={styles.comment}>
              <CommentItem comment={comment} onReply={handleAddComment} />

              {comment.replies?.map((reply: Comment) => (
                <CommentItem key={reply._id} comment={reply} isReply />
              ))}
            </div>
          ))}
      </div>

      <AddComment onAddComment={handleAddComment} />
    </div>
  );
});
